/**
 * Поп-ап «что нового» (#946).
 *
 * Показывается один раз на релиз тому, кто вошёл после выката. Содержание
 * вшито в бандл на сборке (`release.ts`), поэтому открывается сразу, без
 * запроса к сети.
 *
 * Один и тот же компонент служит вебу и встроенному приложению CRM: во
 * втором он живёт внутри нашего iframe и интерфейс портала не перекрывает, а
 * `size="md"` с прокруткой содержимого оставляет крестик достижимым на узком
 * размещении.
 */
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import type { ProductPackageKey } from '@aica/core';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalFooter,
} from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { getDocsUrl } from '@/lib/docs';
import { PACKAGE_LABEL } from '@/lib/product-package-label';
import { resolveWhatsNewLocale } from './release';
import { tierNoteFor } from './tierNote';
import type { WhatsNewRelease } from '@aica/api-types';

/**
 * Раздел тарифов целиком: `section=billing` открывает биллинг на вкладке
 * «Использование», а обещание кнопки — планы. Адрес собран так же, как в
 * остальном продукте (`ClientDashboard`, `SubscriptionSection`).
 */
const PLANS_ROUTE = '/settings?section=billing&tab=plan';

/**
 * Публичная страница «Что нового» — та же курированная запись, целиком.
 *
 * Адрес строится `getDocsUrl`: документация живёт на маркетинговом домене, а
 * не на домене приложения, и правила про префикс локали и завершающий слеш
 * заданы конфигурацией Astro. Свой шаблон разошёлся бы с ними молча.
 */
function changelogUrl(language: string | undefined): string {
  return `${getDocsUrl('changelog', language)}?utm_source=app&utm_medium=whats_new&utm_campaign=release`;
}

export interface WhatsNewDialogProps {
  release: WhatsNewRelease;
  /**
   * Тариф организации, ТОЛЬКО когда он достоверен.
   *
   * `null` — тариф неизвестен (организация не выбрана, воркер ещё без поля),
   * и тогда пометки о тарифе нет вовсе: сказать «доступно на Pro и выше»
   * организации, у которой Pro уже есть, — ложное обещание апгрейда, и
   * исправить его после показа нельзя.
   */
  tier: ProductPackageKey | null;
  /**
   * Может ли человек открыть страницу тарифов (`canSeeBilling`: владелец
   * аккаунта и владелец платформы).
   *
   * Пометку о тарифе видит КАЖДАЯ роль — смысл канала в том, чтобы узнать о
   * возможности, которой нет. А кнопка «Посмотреть тарифы» для admin, agent и
   * supervisor вела бы на «Доступ запрещён»: биллинг им закрыт. Вместо неё
   * они видят, к кому идти (находка Codex P2 на #996).
   */
  canViewPlans: boolean;
  /** Закрытие — оно же и есть отметка «видел»: другого способа закрыть нет. */
  onClose: () => void;
  /**
   * Переход по пункту внутрь продукта; без него пункт остаётся текстом.
   * Принимает готовый адрес со всеми параметрами — так же, как остальные
   * вызовы `appNavigate` в продукте.
   */
  onNavigate?: (to: string) => void;
}

export function WhatsNewDialog({
  release,
  tier,
  canViewPlans,
  onClose,
  onNavigate,
}: WhatsNewDialogProps) {
  const { t, i18n } = useTranslation();
  const locale = resolveWhatsNewLocale(i18n.language);

  return (
    <Modal
      open
      onOpenChange={(open) => {
        // Крестик, Esc и клик мимо ведут туда же, куда кнопка: закрыт —
        // значит увиден. Иначе человек, закрывший поп-ап Esc'ом, получил бы
        // его снова при следующем входе.
        if (!open) onClose();
      }}
    >
      <ModalContent size="md" aria-describedby={undefined}>
        <ModalHeader>
          <ModalTitle>{t('common:whatsNew.title')}</ModalTitle>
          <p className="text-sm text-fg-muted">
            {t('common:whatsNew.versionLabel', { version: release.version })}
          </p>
        </ModalHeader>

        <div className="max-h-[60vh] space-y-6 overflow-y-auto py-2">
          {release.items.map((item, index) => {
            const copy = item[locale];
            const requiredPackage = tier ? tierNoteFor(item.feature, tier) : null;

            return (
              <div key={`${release.version}-${index}`} className="space-y-2">
                <h3 className="text-base font-medium text-fg">{copy.title}</h3>
                <p className="text-sm leading-relaxed text-fg-muted">{copy.text}</p>

                {requiredPackage && (
                  // Пункт НЕ скрыт: смысл канала в том, что человек узнаёт о
                  // возможности, которой у него нет.
                  <p className="text-xs text-fg-subtle">
                    {t('common:whatsNew.tierNote', {
                      package: PACKAGE_LABEL[requiredPackage],
                    })}
                    {' · '}
                    {canViewPlans && onNavigate ? (
                      <button
                        type="button"
                        className="underline underline-offset-2 hover:text-fg"
                        onClick={() => onNavigate(PLANS_ROUTE)}
                      >
                        {t('common:whatsNew.tierCta')}
                      </button>
                    ) : (
                      t('common:whatsNew.tierAskOwner')
                    )}
                  </p>
                )}

                {item.href && onNavigate && !item.feature && (
                  // Только у пункта БЕЗ тарифной возможности.
                  //
                  // Доступность возможности решает не матрица пакетов, а
                  // флаг (`product-features.ts`: «матрица пакетов сама по себе
                  // НИЧЕГО не запрещает»), и прочитать флаг можно не везде:
                  // во встроенной оболочке нет ни провайдера флагов, ни
                  // провайдера тарифа. Значит «доступна ли она ИМЕННО этому
                  // клиенту» здесь неизвестно — и вести его на экран, где
                  // возможности может не оказаться, значит подменить новость
                  // тупиком. Пункт остаётся текстом с пометкой о тарифе.
                  <button
                    type="button"
                    className="text-xs text-primary underline underline-offset-2"
                    onClick={() => onNavigate(item.href as string)}
                  >
                    {t('common:whatsNew.open')}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <ModalFooter className="items-center justify-between">
          <a
            className="inline-flex items-center gap-1 text-xs text-fg-muted underline underline-offset-2 hover:text-fg"
            href={changelogUrl(i18n.language)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('common:whatsNew.more')}
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
          </a>
          <Button onClick={onClose}>{t('common:whatsNew.close')}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
